import { tz } from "@date-fns/tz";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useCallback, useState } from "react";

import { axiosInstance, PRICES } from "api";
import { getSelectedPowerPlant, useAppSelector } from "store";

export interface IntervalPrice {
  time: string; // "HH:mm"
  // PSE's preliminary imbalance price, ~27 min after the slot starts.
  pse: number | null;
  // The settled value, published the next day.
  pseActual: number | null;
  rdn: number | null;
}

export interface PricesData {
  date: string;
  prices: IntervalPrice[];
  lastPublishedAt: string | null;
  sources: {
    pse: string | null;
    pseActual: string | null;
    rdn: string | null;
  };
}

export interface PricesResponse {
  today: PricesData;
  tomorrow: PricesData;
}

// `manual` marks a refresh the user asked for, which permits the backend to
// hit TGE/PSE sooner than the automatic cadence does.
const fetchPrices = async (dateStr: string, manual = false) => {
  const { data } = await axiosInstance.get<PricesResponse>(PRICES.PRICES, {
    params: { date: dateStr, ...(manual ? { refresh: 1 } : {}) },
  });
  return data;
};

// Always resolve to a real date string so the queryKey matches across all
// call sites for the same day (widget, chart, table, last-updated readout).
// Without this, useGetPrices() and useGetPrices(today) would be separate
// cache entries and refetch on independent timers, drifting out of sync.
// The fallback "today" is resolved in the plant's timezone — otherwise a
// viewer ahead of the plant near midnight would request the wrong day.
export const usePricesDateStr = (date?: Date) => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  return format(date ?? new Date(), "yyyy-MM-dd", {
    in: tz(selectedPowerPlant?.timezone || "Europe/Warsaw"),
  });
};

export const useGetPrices = (date?: Date) => {
  const dateStr = usePricesDateStr(date);
  return useQuery<PricesResponse>({
    queryKey: [PRICES.PRICES, dateStr],
    queryFn: () => fetchPrices(dateStr),
    refetchInterval: 60_000,
    staleTime: 30_000,
    // Focus refetching is off app-wide, but prices are the screen people
    // leave open and come back to. The interval is paused while the tab is
    // hidden (and frozen outright in a backgrounded PWA), so without these
    // two the page stays on whatever it fetched before it lost focus.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useRefreshPrices = (date?: Date) => {
  const queryClient = useQueryClient();
  const dateStr = usePricesDateStr(date);
  const todayStr = usePricesDateStr();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // The widget always shows today while the chart may be on another day —
      // refresh both so the page never mixes two vintages.
      await Promise.all(
        Array.from(new Set([dateStr, todayStr])).map((d) =>
          queryClient.fetchQuery({
            queryKey: [PRICES.PRICES, d],
            queryFn: () => fetchPrices(d, true),
            staleTime: 0,
          }),
        ),
      );
    } catch (error) {
      // Keep the last good data on screen; the freshness readout goes on
      // showing its real age, so a failed refresh doesn't look like a fresh one.
      console.error("Prices refresh failed", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [dateStr, queryClient, todayStr]);

  return { refresh, isRefreshing };
};

export interface PricesAvailability {
  minDate: string | null;
}

export const useGetPricesAvailability = () =>
  useQuery<PricesAvailability>({
    queryKey: [PRICES.AVAILABILITY],
    queryFn: async () => {
      const { data } = await axiosInstance.get<PricesAvailability>(
        PRICES.AVAILABILITY,
      );
      return data;
    },
    staleTime: 60 * 60 * 1000,
  });
